"use client";

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Copy01Icon,
  ViewIcon,
  ViewOffIcon,
  PlusSignIcon,
  ArrowUpRight01Icon,
  MoreHorizontalIcon,
  Wallet01Icon,
  TrendingUpIcon,
  ListChecksIcon,
  CheckmarkCircle02Icon,
  Cancel01Icon,
  GoalIcon,
  Clock01Icon,
  AlertDiamondIcon,
  Alert01Icon,
} from "@hugeicons/core-free-icons";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { get, put, getStoredUser, updateStoredUser } from "../../utils/service";

interface DashboardStats {
  currency: string;
  total_loans: number;
  active_loans: number;
  total_principal_outstanding: string;
  total_expected_returns: string;
  total_amount_due_outstanding: string;
  due_within_7_days: number;
  expiring_today: number;
  overdue_loans: number;
  overdue_amount: string;
  redeemed_loans: number;
  total_redeemed_amount: string;
}

interface DueSoonLoan {
  id: string;
  due_date: string;
  days_remaining: number;
  days_overdue: number;
  current_amount_due: string;
  currency: string;
  effective_status: string;
  client?: { name: string; phone: string | null };
}

interface RevenueChartPoint {
  month: string;
  principal_disbursed: number;
  expected_returns: number;
  loans_count: number;
}

interface DashboardData {
  stats: DashboardStats;
  due_soon: DueSoonLoan[];
  recent_activity: unknown[];
  revenue_chart: RevenueChartPoint[];
}

interface DashboardResponse {
  success: boolean;
  data: DashboardData;
}

const formatLabel = (value: string) =>
  value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

// A fully repaid loan is shown as "Paid" (the stored status value stays "redeemed").
const statusLabel = (status: string) => (status === "redeemed" ? "Paid" : formatLabel(status));

const formatCurrency = (amount: string | number, currency: string = "UGX") => {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(n)) return `${currency} 0`;
  return `${currency} ${n.toLocaleString()}`;
};

const STATUS_COLORS: Record<string, string> = {
  active: "bg-gray-100 text-gray-800",
  due_soon: "bg-amber-100 text-amber-800",
  expiring_today: "bg-orange-100 text-orange-800",
  expired: "bg-red-100 text-red-800",
  redeemed: "bg-green-100 text-green-800",
};

// recharts/SVG fill props can't take Tailwind classes, so these are raw hex — but they're
// exactly Tailwind's own gray-400/amber-500/red-500/green-500, matching the STATUS_COLORS
// badges above rather than being arbitrary colors.
const STATUS_DOTS: Record<string, string> = {
  active: "#9ca3af",
  due_soon: "#f59e0b",
  expired: "#ef4444",
  redeemed: "#22c55e",
};

const SkeletonBlock = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse rounded-3xl bg-gray-100 ${className}`} />
);

type StatusFilter = "all" | "active" | "overdue";

function LoansCard({ data }: { data: DashboardData }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [hidden, setHidden] = useState(() => getStoredUser()?.hide_balance ?? false);
  const currency = data.stats.currency || "UGX";

  const toggleHidden = async () => {
    const next = !hidden;
    setHidden(next);
    updateStoredUser({ hide_balance: next });
    try {
      await put("/auth/profile", { hide_balance: next });
    } catch (error) {
      console.error("Error saving balance visibility preference:", error);
    }
  };

  const amount = useMemo(() => {
    if (filter === "active") {
      // Active portfolio approximated by total outstanding scaled to active share
      return data.stats.total_principal_outstanding;
    }
    if (filter === "overdue") {
      return data.stats.overdue_amount;
    }
    return data.stats.total_principal_outstanding;
  }, [filter, data.stats]);

  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-4 flex flex-col h-full">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center rounded-full bg-gray-100 p-1">
          {(["all", "active", "overdue"] as StatusFilter[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                filter === tab ? "bg-primary text-white" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab === "all" ? "All" : tab === "active" ? "Active" : "Overdue"}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span>Total Loans</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 font-semibold text-gray-800">
            {data.stats.total_loans}
            <HugeiconsIcon icon={Copy01Icon} size={12} className="text-gray-400" />
          </span>
        </div>
      </div>

      <div className="mt-3">
        <p className="text-xs font-medium text-gray-500">Portfolio Outstanding</p>
        <div className="mt-1 flex items-center gap-3">
          <p className={`text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 ${hidden ? "blur-md select-none" : ""}`}>
            {formatCurrency(amount, currency)}
          </p>
          <button
            onClick={toggleHidden}
            className="shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
            aria-label={hidden ? "Show amount" : "Hide amount"}
          >
            {hidden ? <HugeiconsIcon icon={ViewOffIcon} size={16} /> : <HugeiconsIcon icon={ViewIcon} size={16} />}
          </button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          onClick={() => navigate("/loans?new=1")}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
        >
          <HugeiconsIcon icon={PlusSignIcon} size={16} />
          New Loan
        </button>
        <button
          onClick={() => navigate("/loans")}
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          <HugeiconsIcon icon={Wallet01Icon} size={16} />
          Pay Loan
        </button>
      </div>

      <div className="mt-auto flex justify-end pt-3">
        <button
          onClick={() => navigate("/loans")}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white hover:bg-primary/90"
          aria-label="View all loans"
        >
          <HugeiconsIcon icon={ArrowUpRight01Icon} size={20} />
        </button>
      </div>
    </div>
  );
}

function FollowUpCard({ data }: { data: DashboardData }) {
  const navigate = useNavigate();
  const currency = data.stats.currency || "UGX";

  const mostUrgent = useMemo(() => {
    if (!data.due_soon.length) return null;
    const score = (l: DueSoonLoan) => (l.days_overdue > 0 ? -l.days_overdue - 1000 : l.days_remaining);
    return [...data.due_soon].sort((a, b) => score(a) - score(b))[0];
  }, [data.due_soon]);

  return (
    <div className="rounded-3xl bg-gradient-to-br from-primary to-primary-scale-400 p-4 flex flex-col h-full text-white">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/10 pl-3 pr-1 py-1">
          <span className="text-xs font-semibold tracking-wide">FOLLOW UP</span>
          <button
            onClick={() => navigate("/loans?status=due_soon")}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20 hover:bg-white/30"
            aria-label="View follow ups"
          >
            <HugeiconsIcon icon={PlusSignIcon} size={14} />
          </button>
        </div>
        <button className="text-white/70 hover:text-white" aria-label="More options">
          <HugeiconsIcon icon={MoreHorizontalIcon} size={20} />
        </button>
      </div>

      <div className="mt-3">
        <p className="text-xs font-medium text-white/80">Needs Attention</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <p className="text-2xl sm:text-3xl font-bold tracking-tight">
            {formatCurrency(data.stats.overdue_amount, currency)}
          </p>
          <span className="rounded-full bg-white/20 px-2.5 py-1 text-[11px] font-semibold">
            {data.stats.due_within_7_days} due soon
          </span>
        </div>
      </div>

      {mostUrgent && (
        <button
          onClick={() => navigate(`/loans?highlight=${mostUrgent.id}`)}
          className="mt-3 flex-1 w-full rounded-2xl bg-white p-3.5 text-left text-gray-900 hover:bg-white/90 transition-colors"
        >
          <p className="text-[11px] font-medium text-gray-500">Most urgent</p>
          <div className="mt-1 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold truncate">{mostUrgent.client?.name || "—"}</p>
            <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLORS[mostUrgent.effective_status] || "bg-gray-100 text-gray-800"}`}>
              {statusLabel(mostUrgent.effective_status)}
            </span>
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <p className="text-xs text-gray-500">
              {mostUrgent.days_overdue > 0 ? `${mostUrgent.days_overdue}d overdue` : mostUrgent.days_remaining > 0 ? `${mostUrgent.days_remaining}d left` : "Due today"}
            </p>
            <p className="text-sm font-semibold text-gray-900">{formatCurrency(mostUrgent.current_amount_due, mostUrgent.currency || currency)}</p>
          </div>
        </button>
      )}
    </div>
  );
}

function LoanLifecycleStats({ data }: { data: DashboardData }) {
  const navigate = useNavigate();
  const stats = [
    { label: "Due Soon", value: data.stats.due_within_7_days, icon: Clock01Icon, tone: "text-amber-600 bg-amber-50", path: "/loans?status=due_soon" },
    { label: "Expiring Today", value: data.stats.expiring_today, icon: AlertDiamondIcon, tone: "text-orange-600 bg-orange-50", path: "/loans?status=expiring_today" },
    { label: "Overdue", value: data.stats.overdue_loans, icon: Alert01Icon, tone: "text-red-600 bg-red-50", path: "/loans?status=expired" },
    { label: "Paid", value: data.stats.redeemed_loans, icon: CheckmarkCircle02Icon, tone: "text-green-600 bg-green-50", path: "/loans?status=redeemed" },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
      {stats.map((s) => (
        <button
          key={s.label}
          onClick={() => navigate(s.path)}
          className="rounded-3xl border border-gray-200 bg-white p-4 text-left hover:border-gray-300 transition-colors"
        >
          <span className={`flex h-9 w-9 items-center justify-center rounded-full ${s.tone}`}>
            <HugeiconsIcon icon={s.icon} size={18} />
          </span>
          <p className="mt-3 text-2xl font-bold tracking-tight text-gray-900">{s.value}</p>
          <p className="mt-0.5 text-xs font-medium text-gray-500">{s.label}</p>
        </button>
      ))}
    </div>
  );
}

function LoanStatusCard({ data }: { data: DashboardData }) {
  const monthLabel = new Date().toLocaleString("en", { month: "long", year: "numeric" });
  const total = Math.max(data.stats.total_loans, 1);
  const overduePct = Math.round((data.stats.overdue_loans / total) * 100);
  const needsFollowUp = data.stats.due_within_7_days + data.stats.expiring_today;

  const active = data.stats.active_loans;
  const dueSoon = data.stats.due_within_7_days;
  const overdue = data.stats.overdue_loans;
  const redeemed = data.stats.redeemed_loans;

  const ringData = [
    { name: "overdue", value: data.stats.overdue_loans },
    { name: "rest", value: Math.max(total - data.stats.overdue_loans, 0) },
  ];

  const legend = [
    { key: "active", label: "Active", value: active },
    { key: "due_soon", label: "Due Soon", value: dueSoon },
    { key: "expired", label: "Overdue", value: overdue },
    { key: "redeemed", label: "Paid", value: redeemed },
  ];

  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-4 flex flex-col h-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-700">
            <HugeiconsIcon icon={ListChecksIcon} size={16} />
          </span>
          <h3 className="text-sm font-semibold text-gray-900">Loan Status</h3>
        </div>
        <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-medium text-gray-600">
          {monthLabel}
        </span>
      </div>

      <div className="mt-2 relative h-28 w-28 mx-auto">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={ringData}
              dataKey="value"
              innerRadius="72%"
              outerRadius="100%"
              startAngle={90}
              endAngle={-270}
              stroke="none"
            >
              {/* recharts needs raw hex for SVG fill; these are Tailwind's red-500 and gray-200 */}
              <Cell fill="#ef4444" />
              <Cell fill="#e5e7eb" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-2xl font-bold text-gray-900">{data.stats.overdue_loans}</p>
          <p className="text-[10px] text-gray-500">overdue</p>
        </div>
      </div>

      <div className="mt-3 flex justify-center">
        <span
          className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
            overduePct >= 30 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
          }`}
        >
          {overduePct}% overdue
        </span>
      </div>

      <div className="mt-3 flex justify-center">
        <span className="rounded-full bg-gray-100 px-3 py-1.5 text-[11px] text-gray-600 text-center">
          {needsFollowUp} loan{needsFollowUp === 1 ? "" : "s"} need follow-up
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
        {legend.map((item) => (
          <div key={item.key} className="flex items-center gap-2 text-xs">
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: STATUS_DOTS[item.key] }} />
            <span className="text-gray-600 truncate">{item.label}</span>
            <span className="ml-auto font-semibold text-gray-900">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExpectedReturnsCard({ data }: { data: DashboardData }) {
  const currency = data.stats.currency || "UGX";
  const total = Math.max(data.stats.total_loans, 1);

  const delta = useMemo(() => {
    const m = data.revenue_chart;
    if (!m || m.length < 2) return null;
    const last = m[m.length - 1];
    const prev = m[m.length - 2];
    if (!prev || prev.principal_disbursed <= 0) return null;
    return ((last.principal_disbursed - prev.principal_disbursed) / prev.principal_disbursed) * 100;
  }, [data.revenue_chart]);

  const bars = [
    { key: "active", label: "Active", value: data.stats.active_loans, color: "#9ca3af" },
    { key: "due_soon", label: "Due Soon", value: data.stats.due_within_7_days, color: "#f59e0b" },
    { key: "expired", label: "Overdue", value: data.stats.overdue_loans, color: "#ef4444" },
    { key: "redeemed", label: "Paid", value: data.stats.redeemed_loans, color: "#22c55e" },
  ];

  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-4 flex flex-col h-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-700">
            <HugeiconsIcon icon={TrendingUpIcon} size={16} />
          </span>
          <h3 className="text-sm font-semibold text-gray-900">Expected Returns</h3>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-[11px] font-semibold text-green-700">
          <HugeiconsIcon icon={GoalIcon} size={12} />
          {formatCurrency(data.stats.total_expected_returns, currency)}
        </span>
      </div>

      <div className="mt-4">
        <p className="text-3xl font-bold tracking-tight text-gray-900">
          {formatCurrency(data.stats.total_amount_due_outstanding, currency)}
        </p>
        {delta !== null && (
          <p className={`mt-1 text-xs font-medium ${delta >= 0 ? "text-green-600" : "text-red-600"}`}>
            {delta >= 0 ? "+" : ""}
            {delta.toFixed(0)}% vs last month
          </p>
        )}
      </div>

      <div className="mt-3 space-y-2.5">
        {bars.map((bar) => {
          const pct = Math.round((bar.value / total) * 100);
          return (
            <div key={bar.key}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-600">{bar.label}</span>
                <span className="font-semibold text-gray-900">{bar.value}</span>
              </div>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${Math.max(pct, 2)}%`, background: bar.color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function urgencyLabel(loan: DueSoonLoan) {
  if (loan.days_overdue > 0) return `${loan.days_overdue} day${loan.days_overdue === 1 ? "" : "s"} overdue`;
  if (loan.days_remaining === 0) return "Due today";
  return `Due in ${loan.days_remaining} day${loan.days_remaining === 1 ? "" : "s"}`;
}

function NeedsFollowUpCard({ data }: { data: DashboardData }) {
  const navigate = useNavigate();
  const currency = data.stats.currency || "UGX";
  const loans = data.due_soon.slice(0, 5);

  const sorted = [...loans].sort((a, b) => {
    const score = (l: DueSoonLoan) => (l.days_overdue > 0 ? -l.days_overdue - 1000 : l.days_remaining);
    return score(a) - score(b);
  });

  const [top, ...rest] = sorted;

  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-4 flex flex-col h-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-700">
            <HugeiconsIcon icon={Wallet01Icon} size={16} />
          </span>
          <h3 className="text-sm font-semibold text-gray-900">Needs Follow-Up</h3>
        </div>
      </div>

      <div className="mt-2 flex-1 space-y-1">
        {!top && (
          <p className="py-8 text-center text-xs text-gray-500">Nothing needs follow-up right now.</p>
        )}
        {top && (
          <div
            className={`flex items-center gap-3 rounded-xl border p-2.5 ${
              top.days_overdue > 0 ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"
            }`}
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
              {(top.client?.name || "?").charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900">{top.client?.name || "Unknown client"}</p>
              <p className="text-[11px] text-gray-500">{urgencyLabel(top)}</p>
            </div>
            <p className={`text-sm font-semibold shrink-0 ${top.days_overdue > 0 ? "text-red-600" : "text-green-600"}`}>
              {formatCurrency(top.current_amount_due, top.currency || currency)}
            </p>
            <div className="flex shrink-0 items-center gap-1">
              <button
                onClick={() => navigate(`/loans?highlight=${top.id}`)}
                className="flex h-7 w-7 items-center justify-center rounded-full bg-green-600 text-white hover:bg-green-700"
                aria-label="Pay loan"
              >
                <HugeiconsIcon icon={CheckmarkCircle02Icon} size={14} />
              </button>
              <button
                onClick={() => navigate(`/loans?highlight=${top.id}`)}
                className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300"
                aria-label="View loan"
              >
                <HugeiconsIcon icon={Cancel01Icon} size={14} />
              </button>
            </div>
          </div>
        )}

        {rest.map((loan) => (
          <button
            key={loan.id}
            onClick={() => navigate(`/loans?highlight=${loan.id}`)}
            className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left hover:bg-gray-50"
          >
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                STATUS_COLORS[loan.effective_status] || "bg-gray-100 text-gray-800"
              }`}
            >
              {(loan.client?.name || "?").charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900">{loan.client?.name || "Unknown client"}</p>
              <p className="text-[11px] text-gray-500">{urgencyLabel(loan)}</p>
            </div>
            <p className={`text-sm font-semibold shrink-0 ${loan.days_overdue > 0 ? "text-red-600" : "text-green-600"}`}>
              {formatCurrency(loan.current_amount_due, loan.currency || currency)}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

function EnhancedDashboardContent() {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await get<DashboardResponse>("/dashboard");
      if (response.success) {
        setDashboardData(response.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !dashboardData) {
    return (
      <div className="min-h-screen p-4 sm:p-0 pb-8 sm:pb-8 space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <SkeletonBlock className="lg:col-span-3 h-80" />
          <SkeletonBlock className="lg:col-span-2 h-80" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <SkeletonBlock className="h-96" />
          <SkeletonBlock className="h-96" />
          <SkeletonBlock className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-0 pb-8 sm:pb-8">
      <div className="w-full space-y-5">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-stretch">
          <div className="lg:col-span-3">
            <LoansCard data={dashboardData} />
          </div>
          <div className="lg:col-span-2">
            <FollowUpCard data={dashboardData} />
          </div>
        </div>

        <LoanLifecycleStats data={dashboardData} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          <LoanStatusCard data={dashboardData} />
          <ExpectedReturnsCard data={dashboardData} />
          <NeedsFollowUpCard data={dashboardData} />
        </div>
      </div>
    </div>
  );
}

export function Dashboard() {
  return (
    <div className="w-full h-full">
      <EnhancedDashboardContent />
    </div>
  );
}

export default Dashboard;

export { formatCurrency, formatLabel };
